import './style.css';

import { graphql, Link as GatsbyLink, useStaticQuery } from 'gatsby';
import { AnchorLink } from 'gatsby-plugin-anchor-links';
import React, { forwardRef, LegacyRef, useCallback } from 'react';
import Obfuscate from 'react-obfuscate';

import { ButtonTargetStyles } from '../../constants/ui';
import { addTrailingSlash, removeDoubleSlash } from '../../utils/links';
import { tw } from '../../utils/tailwind';

interface IStyledLinkProps {
  anchorId?: string | null;
  children?: React.ReactNode;
  className?: string;
  href?: string;
  isEmail?: boolean;
  isPhone?: boolean;
  onClick?: () => void;
  shouldObfuscate?: boolean;
  state?: {
    idCentre?: string;
    kinequickSpeciality?: string;
    kinequickType?: string;
    services?: (string | undefined)[];
  };
  style?: React.CSSProperties;
  target?: ButtonTargetStyles;
  to?: string | null;
  variant?: 'internalLink' | 'navigationLink';
}

const StyledLink = forwardRef<HTMLAnchorElement, IStyledLinkProps>(
  (
    {
      anchorId,
      children,
      className,
      href,
      isEmail = false,
      isPhone = false,
      onClick,
      shouldObfuscate = false,
      state,
      style,
      target,
      to = null,
      variant = 'internalLink',
    },
    ref
  ) => {
    const query = useStaticQuery(graphql`
      query SiteMetaData {
        site {
          siteMetadata {
            siteUrl
            wordpressUrl
          }
        }
      }
    `);
    const siteMetadata = query.site.siteMetadata;
    // TODO : refactor
    const cleanLink = useCallback(
      (path: string) => {
        const pathWithoutOwnDomain = path
          .replace(siteMetadata?.siteUrl ?? '', '')
          .replace(siteMetadata?.wordpressUrl ?? '', '');
        const pathWithTrailingSlash = addTrailingSlash(pathWithoutOwnDomain);
        const pathWithoutDoubleLeadingSlash = removeDoubleSlash(
          pathWithTrailingSlash
        );
        const pathWithAlwaysOneLeadingSlash =
          pathWithoutDoubleLeadingSlash.charAt(0) !== '/'
            ? `/${pathWithoutDoubleLeadingSlash}`
            : pathWithoutDoubleLeadingSlash;
        return pathWithAlwaysOneLeadingSlash;
      },
      [to]
    );
    const cleanedInternalLink = to && cleanLink(to);

    const CLASSNAME_BY_TYPE = {
      internalLink: 'internal-link ',
      navigationLink: '!text-xl !uppercase  !font-label',
    };
    const getRelevantLinkComponent = ({
      anchor,
      externalLink,
      internalLink,
    }: {
      anchor?: string | null;
      externalLink?: string | null;
      internalLink?: string | null;
    }) => {
      if (shouldObfuscate) {
        return (
          <Obfuscate
            {...(isEmail ? { email: to } : {})}
            {...(isPhone ? { tel: to } : {})}
            linkText="/obfuscated"
            target="_blank"
            style={{ ...style, direction: 'ltr' }}
            obfuscateChildren={false}
            className={tw('border-b-0', CLASSNAME_BY_TYPE[variant], className)}
            onClick={onClick}
          >
            {children}
          </Obfuscate>
        );
      } else if (internalLink) {
        return (
          <GatsbyLink
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore https://github.com/gatsbyjs/gatsby/issues/34325
            ref={ref}
            state={state}
            to={cleanedInternalLink}
            target={target}
            className={tw(
              'border-b-0',
              CLASSNAME_BY_TYPE.internalLink,
              className
            )}
            style={style}
            onClick={onClick}
          >
            {children}
          </GatsbyLink>
        );
      } else if (externalLink) {
        return (
          <a
            href={href}
            ref={ref}
            target="_blank"
            rel="noopener noreferrer"
            className={tw(
              'border-b-0',
              CLASSNAME_BY_TYPE.internalLink,
              className
            )}
          >
            {children}
          </a>
        );
      } else if (anchor) {
        return (
          <AnchorLink
            to={anchor}
            className={tw(
              'text-inherit border-b-0',
              CLASSNAME_BY_TYPE[variant],
              className
            )}
          >
            {children}
          </AnchorLink>
        );
      } else {
        return (
          <div
            ref={ref as unknown as LegacyRef<HTMLDivElement>}
            tabIndex={0}
            className={tw(
              'border-b-0',
              CLASSNAME_BY_TYPE.internalLink,
              className
            )}
            onClick={onClick}
          >
            {children}
          </div>
        );
      }
    };
    const linkComponent = getRelevantLinkComponent({
      anchor: anchorId,
      externalLink: href,
      internalLink: cleanedInternalLink,
    });
    return linkComponent;
  }
);

StyledLink.displayName = 'StyledLink';

export default StyledLink;
